import React from 'react';
import { CheckCircle2, AlertTriangle, XCircle, FileQuestion, X, FileSpreadsheet } from 'lucide-react';

/**
 * Récapitulatif visuel post-import multi-PDL TURPE.
 * Affiche des badges et la liste des fichiers reconnus / manquants / en erreur.
 */
const ImportRecapPanel = ({ recap, onDismiss }) => {
  if (!recap) return null;

  const { mode, filesTotal, pdlsConfigured, pdlsInFile, imported, notDetected, notConfigured, missing, errors } = recap;
  const reconnus = imported.length;
  const allOk = reconnus === pdlsConfigured && (notDetected?.length || 0) === 0 && (notConfigured?.length || 0) === 0 && (errors?.length || 0) === 0;

  const bannerColor = allOk
    ? 'bg-green-50 border-green-300'
    : reconnus > 0
      ? 'bg-yellow-50 border-yellow-300'
      : 'bg-red-50 border-red-300';

  const bannerIcon = allOk
    ? <CheckCircle2 className="w-6 h-6 text-green-600" />
    : reconnus > 0
      ? <AlertTriangle className="w-6 h-6 text-yellow-600" />
      : <XCircle className="w-6 h-6 text-red-600" />;

  const titre = mode === 'multi-pdl-file'
    ? `Import du fichier multi-PDL : ${reconnus}/${pdlsConfigured} PDL importés`
    : `Import groupé : ${reconnus}/${pdlsConfigured} PDL reconnus automatiquement`;

  return (
    <div className={`border-2 rounded-lg p-5 my-4 ${bannerColor}`} data-testid="import-recap-panel">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          {bannerIcon}
          <div>
            <div className="font-semibold text-[#191D1E]">{titre}</div>
            <div className="text-xs text-[#687175] mt-0.5">
              {mode === 'multi-pdl-file'
                ? <>1 fichier déposé{pdlsInFile != null ? ` · ${pdlsInFile} PDL détectés` : ''} · {pdlsConfigured} PDL configurés</>
                : <>{filesTotal} fichier{filesTotal > 1 ? 's' : ''} déposé{filesTotal > 1 ? 's' : ''} · {pdlsConfigured} PDL configurés</>
              }
            </div>
          </div>
        </div>
        <button onClick={onDismiss} className="p-1 rounded hover:bg-white/50" aria-label="Fermer le récapitulatif" data-testid="dismiss-import-recap">
          <X className="w-4 h-4 text-[#687175]" />
        </button>
      </div>

      {/* Badges synthétiques */}
      <div className="flex flex-wrap gap-2 mb-4">
        <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 inline-flex items-center gap-1">
          <CheckCircle2 className="w-3 h-3" /> {reconnus} importé{reconnus > 1 ? 's' : ''}
        </span>
        {missing?.length > 0 && (
          <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800 inline-flex items-center gap-1">
            <FileQuestion className="w-3 h-3" /> {missing.length} à importer
          </span>
        )}
        {notDetected?.length > 0 && (
          <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 inline-flex items-center gap-1">
            <AlertTriangle className="w-3 h-3" /> {notDetected.length} PDL non détecté{notDetected.length > 1 ? 's' : ''}
          </span>
        )}
        {notConfigured?.length > 0 && (
          <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800 inline-flex items-center gap-1">
            <AlertTriangle className="w-3 h-3" /> {notConfigured.length} PDL non configuré{notConfigured.length > 1 ? 's' : ''}
          </span>
        )}
        {errors?.length > 0 && (
          <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800 inline-flex items-center gap-1">
            <XCircle className="w-3 h-3" /> {errors.length} erreur{errors.length > 1 ? 's' : ''}
          </span>
        )}
      </div>

      {/* Listes détaillées */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {imported.length > 0 && (
          <div className="bg-white rounded-lg border border-green-200 p-3" data-testid="recap-imported">
            <div className="text-xs font-semibold uppercase text-green-700 mb-2 flex items-center gap-1.5">
              <CheckCircle2 className="w-3.5 h-3.5" /> Importés ({imported.length})
            </div>
            <ul className="space-y-1.5 text-sm">
              {imported.map((it) => (
                <li key={it.pdl} className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-mono font-medium text-[#191D1E] truncate">{it.pdl}</div>
                    {it.fileName && mode === 'multi-files' && (
                      <div className="text-xs text-[#687175] truncate flex items-center gap-1">
                        <FileSpreadsheet className="w-3 h-3" /> {it.fileName}
                      </div>
                    )}
                  </div>
                  <div className="text-xs text-[#687175] whitespace-nowrap">
                    {it.nbPoints.toLocaleString('fr-FR')} pts
                    {it.hasReactive && <span className="ml-1 text-blue-600">· réactif</span>}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}

        {missing?.length > 0 && (
          <div className="bg-white rounded-lg border border-orange-200 p-3" data-testid="recap-missing">
            <div className="text-xs font-semibold uppercase text-orange-700 mb-2 flex items-center gap-1.5">
              <FileQuestion className="w-3.5 h-3.5" /> À importer manuellement ({missing.length})
            </div>
            <ul className="space-y-1 text-sm">
              {missing.map((it) => (
                <li key={it.pdl} className="font-mono text-[#191D1E]">{it.pdl}</li>
              ))}
            </ul>
            <div className="text-xs text-[#687175] mt-2 italic">
              Utilisez le bouton « Import » en face du PDL pour téléverser le fichier individuel.
            </div>
          </div>
        )}

        {notDetected?.length > 0 && (
          <div className="bg-white rounded-lg border border-yellow-200 p-3" data-testid="recap-not-detected">
            <div className="text-xs font-semibold uppercase text-yellow-700 mb-2 flex items-center gap-1.5">
              <AlertTriangle className="w-3.5 h-3.5" /> PDL non détecté dans le fichier
            </div>
            <ul className="space-y-1 text-sm">
              {notDetected.map((it, i) => (
                <li key={i} className="text-[#191D1E] truncate flex items-center gap-1">
                  <FileSpreadsheet className="w-3 h-3 text-[#687175]" /> {it.fileName}
                </li>
              ))}
            </ul>
          </div>
        )}

        {notConfigured?.length > 0 && (
          <div className="bg-white rounded-lg border border-purple-200 p-3" data-testid="recap-not-configured">
            <div className="text-xs font-semibold uppercase text-purple-700 mb-2 flex items-center gap-1.5">
              <AlertTriangle className="w-3.5 h-3.5" /> PDL hors configuration
            </div>
            <ul className="space-y-1 text-sm">
              {notConfigured.map((it, i) => (
                <li key={i} className="text-[#191D1E]">
                  <span className="font-mono">{it.pdl}</span>
                  {it.fileName && <span className="text-xs text-[#687175] ml-1">({it.fileName})</span>}
                </li>
              ))}
            </ul>
            <div className="text-xs text-[#687175] mt-2 italic">
              Ces PDL existent dans le fichier mais ne sont pas configurés dans l'étape 3.
            </div>
          </div>
        )}

        {errors?.length > 0 && (
          <div className="bg-white rounded-lg border border-red-200 p-3 md:col-span-2" data-testid="recap-errors">
            <div className="text-xs font-semibold uppercase text-red-700 mb-2 flex items-center gap-1.5">
              <XCircle className="w-3.5 h-3.5" /> Erreurs de parsing
            </div>
            <ul className="space-y-1 text-sm">
              {errors.slice(0, 5).map((e, i) => (
                <li key={i} className="text-[#191D1E]">
                  <span className="font-medium">{e.fileName} : </span>
                  <span className="text-red-700">{e.message}</span>
                </li>
              ))}
              {errors.length > 5 && (
                <li className="text-xs text-[#687175] italic">... et {errors.length - 5} autre(s)</li>
              )}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

export default ImportRecapPanel;
